import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { logOut } from '../../store/authSlice';
import { resetFilters, resetPagination } from '../../../players/store/actions';
import { setUnreadCount } from '../../../messages/store/conversationsSlice';
import { fetchChecklistNotifications, markTaskNotesRead, markTaskRead, markTaskSeen, setPendingNotesTaskId } from '../../../checklist/store/checklistSlice';
import routes from '../../../routesDefinitions/routes';
import url from '../../../constants/config';
import './AdminSidebar.scss';

const STEP_LABELS = { 1: 'Krok 1', 2: 'Krok 2', 3: 'Krok 3' };

const getLabels = (userRole) => {
  const isCoach = userRole === 'coach';
  return {
    greeting: isCoach ? 'Hi' : 'Cześć',
    notifications: isCoach ? 'Notifications' : 'Powiadomienia',
    noNewNotifications: isCoach ? 'No new notifications' : 'Brak nowych powiadomień',
    recentlyRead: isCoach ? 'Recently read' : 'Ostatnio przeczytane',
    players: isCoach ? 'Players' : 'Zawodnicy',
    availablePlayers: isCoach ? 'Available Players' : 'Dostępni zawodnicy',
    myPlayers: 'Moi zawodnicy',
    favorites: isCoach ? 'Favorites' : 'Ulubieni',
    recommended: isCoach ? 'Recommended' : 'Rekomendowani',
    messages: isCoach ? 'Messages' : 'Wiadomości',
    admin: isCoach ? 'Admin' : 'Administracja',
    createPlayer: isCoach ? 'Create Player' : 'Utwórz zawodnika',
    dashboard: isCoach ? 'Dashboard' : 'Panel',
    activity: isCoach ? 'Activity' : 'Aktywność',
    coaches: isCoach ? 'Coaches' : 'Trenerzy',
    pendingCoaches: 'Pending Coaches',
    checklistTemplate: isCoach ? 'Checklist Template' : 'Szablon listy',
    checklistProgress: isCoach ? 'Checklist Progress' : 'Postęp listy',
    assistants: 'Asystenci',
    dashboardOps: isCoach ? 'Dashboard Ops' : 'Panel operacyjny',
    timeEntries: isCoach ? 'Time Entries' : 'Wpisy czasu',
    documents: isCoach ? 'Documents' : 'Dokumenty',
    pipeline: isCoach ? 'Pipeline' : 'Pipeline',
    pageContent: isCoach ? 'Player Guide' : 'Przewodnik gracza',
    oversightCoach: 'Trener ↔ Sportowiec',
    oversightAssistant: 'Asystent ↔ Sportowiec',
    myTasks: 'Moje zadania',
    workTime: 'Czas pracy',
    settings: isCoach ? 'Settings' : 'Ustawienia',
    logout: isCoach ? 'Logout' : 'Wyloguj',
  };
};

const formatRelative = (dateStr) => {
  const diffMin = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diffMin < 1) return 'Teraz';
  if (diffMin < 60) return `${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} godz.`;
  return `${Math.floor(diffH / 24)} dni`;
};

const AdminSidebar = ({ userName, setIsAuthorized }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userRole = useSelector(({ authorization }) => authorization.userRole);
  const isAssistant = userRole === 'assistant';
  const userEmail = useSelector(({ authorization }) => authorization.userEmail);
  const userPersonalEmail = useSelector(({ authorization }) => authorization.user?.personalEmail);
  const labels = getLabels(userRole);
  const unreadCount = useSelector(({ conversations }) => conversations.unreadCount);
  const adminUnreadCount = useSelector(({ adminConversations }) => adminConversations.adminUnreadCount);
  const oversightUnreadCounts = useSelector(({ adminOversight }) => adminOversight.unreadCounts);
  const checklistUnreadCount = useSelector(({ checklist }) => checklist.checklistUnreadCount);
  const notifications = useSelector(({ checklist }) => checklist.notifications);
  const recentlyRead = useSelector(({ checklist }) => checklist.recentlyRead);
  const totalUnread = checklistUnreadCount;
  const [notifOpen, setNotifOpen] = useState(false);
  const [listTop, setListTop] = useState(0);
  const [pendingCoachesCount, setPendingCoachesCount] = useState(0);
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isAssistant) return;
    axios
      .get(`${url}/admin/coaches/pending`, {
        headers: { Authorization: `Bearer ${window.localStorage.getItem('authorization')}` },
      })
      .then(res => setPendingCoachesCount((res.data || []).length))
      .catch(() => {});
  }, [isAssistant]);

  useEffect(() => {
    if (!notifOpen) return undefined;
    const handleClickOutside = e => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  const handleTabClick = () => {
    dispatch(resetPagination());
    dispatch(resetFilters());
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const handleLogout = () => {
    setIsAuthorized(false);
    dispatch(logOut(history, { key: 'authorization', storage: window.localStorage }));
  };

  const handleBellClick = () => {
    if (!notifOpen) {
      dispatch(fetchChecklistNotifications());
      if (bellRef.current) {
        setListTop(bellRef.current.getBoundingClientRect().bottom);
      }
    }
    setNotifOpen(prev => !prev);
  };

  const handleNotifClick = (n) => {
    const base = isAssistant ? '/admin/my-players' : '/available-players';
    if (n.type === 'new_task') {
      dispatch(markTaskSeen(n.taskId));
      if (n.playerId) {
        history.push(`${base}/${n.playerId}?task=${n.taskId}`);
      } else {
        history.push(isAssistant ? '/admin/assistant-dashboard' : '/admin/assistants');
      }
    } else if (n.type === 'internal_note') {
      dispatch(markTaskNotesRead(n.playerId, n.taskId));
      dispatch(setPendingNotesTaskId(n.taskId));
      history.push(`${base}/${n.playerId}?tab=checklist`);
    } else {
      dispatch(markTaskRead(n.playerId, n.taskId));
      history.push(`${base}/${n.playerId}?task=${n.taskId}`);
    }
    setNotifOpen(false);
  };

  const displayName = userName
    ? userName.charAt(0).toUpperCase() + userName.slice(1)
    : '';

  return (
    <aside className="admin-sidebar">
      <div className="admin-sidebar__sticky">
        <div className="admin-sidebar__greeting">
          {labels.greeting}, {displayName || (userRole === 'coach' ? 'Coach' : 'Admin')}!
        </div>

        {/* Bell / notifications */}
        <div className="admin-sidebar__notif-wrap">
          <button
            ref={bellRef}
            type="button"
            className={`admin-sidebar__link admin-sidebar__bell-btn${notifOpen ? ' admin-sidebar__bell-btn--open' : ''}`}
            onClick={handleBellClick}
          >
            <i className="fas fa-bell" />
            {labels.notifications}
            {totalUnread > 0 && !notifOpen && (
              <span className="admin-sidebar__badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
            )}
            <i
              className={`fas fa-chevron-${notifOpen ? 'up' : 'down'} admin-sidebar__bell-chevron`}
            />
          </button>

          {notifOpen && ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              className="admin-sidebar__notif-list"
              style={{ position: 'fixed', top: listTop, left: '8px', width: '300px', zIndex: 99999 }}
            >
              <div className="admin-sidebar__notif-header">
                <span className="admin-sidebar__notif-title">
                  <i className="fas fa-bell" />
                  {labels.notifications}
                </span>
                {checklistUnreadCount > 0 && (
                  <span className="admin-sidebar__notif-count">{checklistUnreadCount > 99 ? '99+' : checklistUnreadCount}</span>
                )}
              </div>
              {notifications.length === 0 && recentlyRead.length === 0 ? (
                <div className="admin-sidebar__notif-empty">
                  <i className="fas fa-check-circle" />
                  {labels.noNewNotifications}
                </div>
              ) : (
                <>
                  {notifications.map((n, i) => (
                    <button
                      // eslint-disable-next-line react/no-array-index-key
                      key={`unread-${n.taskId}-${i}`}
                      type="button"
                      className="admin-sidebar__notif-item"
                      onClick={() => handleNotifClick(n)}
                    >
                      <div className="admin-sidebar__notif-meta">
                        <span className="admin-sidebar__notif-author">
                          {n.type === 'new_task' ? (
                            <><i className="fas fa-plus-circle" style={{ marginRight: '5px', color: '#22c55e' }} />Nowe zadanie</>
                          ) : n.type === 'internal_note' ? (
                            <><i className="fas fa-sticky-note" style={{ marginRight: '5px', color: '#d97706' }} />{n.author}</>
                          ) : (
                            <><i className="fas fa-user-circle" style={{ marginRight: '5px', color: '#94a3b8' }} />{n.author}</>
                          )}
                        </span>
                        <span className="admin-sidebar__notif-time">{formatRelative(n.createdAt)}</span>
                      </div>
                      <div className="admin-sidebar__notif-task">
                        <span className="admin-sidebar__notif-step">{STEP_LABELS[n.step]}</span>
                        {n.taskLabel}
                      </div>
                      {n.preview && n.type !== 'new_task' && (
                        <div className="admin-sidebar__notif-preview">&ldquo;{n.preview}&rdquo;</div>
                      )}
                    </button>
                  ))}
                  {recentlyRead.length > 0 && (
                    <>
                      <div className="admin-sidebar__notif-divider">
                        <span>{labels.recentlyRead}</span>
                      </div>
                      {recentlyRead.map((n, i) => (
                        <button
                          // eslint-disable-next-line react/no-array-index-key
                          key={`read-${n.taskId}-${i}`}
                          type="button"
                          className="admin-sidebar__notif-item admin-sidebar__notif-item--read"
                          onClick={() => handleNotifClick(n)}
                        >
                          <div className="admin-sidebar__notif-meta">
                            <span className="admin-sidebar__notif-author">
                              <i className="fas fa-user-circle" style={{ marginRight: '5px', color: '#94a3b8' }} />
                              {n.author}
                            </span>
                            <span className="admin-sidebar__notif-time">{formatRelative(n.createdAt)}</span>
                          </div>
                          <div className="admin-sidebar__notif-task">
                            <span className="admin-sidebar__notif-step">{STEP_LABELS[n.step]}</span>
                            {n.taskLabel}
                          </div>
                          {n.preview && (
                            <div className="admin-sidebar__notif-preview">&ldquo;{n.preview}&rdquo;</div>
                          )}
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>,
            document.body
          )}
        </div>

        <nav className="admin-sidebar__nav">
          <div className="admin-sidebar__section-label">{labels.players}</div>

          {!isAssistant && (
            <NavLink to="/available-players" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active" onClick={handleTabClick}>
              <i className="fas fa-search" />
              {labels.availablePlayers}
            </NavLink>
          )}

          {isAssistant && (
            <NavLink to={routes.assistantMyPlayers.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-user-friends" />
              {labels.myPlayers}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/favorite-players" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active" onClick={handleTabClick}>
              <i className="fas fa-bookmark" />
              {labels.favorites}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/recommendations" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-magic" />
              {labels.recommended}
            </NavLink>
          )}

          <NavLink to="/messages" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active" onClick={() => dispatch(setUnreadCount(0))}>
            <i className="fas fa-envelope" />
            {labels.messages}
            {(unreadCount + adminUnreadCount) > 0 && (
              <span className="admin-sidebar__badge">{unreadCount + adminUnreadCount}</span>
            )}
          </NavLink>

          <div className="admin-sidebar__divider" />
          <div className="admin-sidebar__section-label">{labels.admin}</div>

          {!isAssistant && (
            <NavLink to="/create-player" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-plus-circle" />
              {labels.createPlayer}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/admin/dashboard" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-chart-line" />
              {labels.dashboard}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/admin/activity" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-stream" />
              {labels.activity}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/admin/coach-engagement" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-users-cog" />
              {labels.coaches}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/admin/pending-coaches" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-user-clock" />
              {labels.pendingCoaches}
              {pendingCoachesCount > 0 && (
                <span className="admin-sidebar__badge">{pendingCoachesCount}</span>
              )}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/admin/checklist-template" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-layer-group" />
              {labels.checklistTemplate}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/admin/checklist-progress" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-tasks" />
              {labels.checklistProgress}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to={routes.adminAssistants.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-user-cog" />
              {labels.assistants}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to={routes.adminOpsDashboard.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-tachometer-alt" />
              {labels.dashboardOps}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to={routes.adminTimeEntries.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-clock" />
              {labels.timeEntries}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to={routes.adminDocuments.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-folder-open" />
              {labels.documents}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to={routes.adminPipeline.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-funnel-dollar" />
              {labels.pipeline}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to={routes.adminPageContent.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-book-open" />
              {labels.pageContent}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/admin/oversight/coach" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active" onClick={scrollToTop}>
              <i className={`fas ${oversightUnreadCounts.coach > 0 ? 'fa-bell' : 'fa-eye'}`} />
              {labels.oversightCoach}
              {oversightUnreadCounts.coach > 0 && (
                <span className="admin-sidebar__badge">{oversightUnreadCounts.coach > 99 ? '99+' : oversightUnreadCounts.coach}</span>
              )}
            </NavLink>
          )}

          {!isAssistant && (
            <NavLink to="/admin/oversight/assistant" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active" onClick={scrollToTop}>
              <i className={`fas ${oversightUnreadCounts.assistant > 0 ? 'fa-bell' : 'fa-eye'}`} />
              {labels.oversightAssistant}
              {oversightUnreadCounts.assistant > 0 && (
                <span className="admin-sidebar__badge">{oversightUnreadCounts.assistant > 99 ? '99+' : oversightUnreadCounts.assistant}</span>
              )}
            </NavLink>
          )}

          {!isAssistant && (userEmail === 'krolczyk.artur@gmail.com' || userPersonalEmail === 'krolczyk.artur@gmail.com') && (
            <NavLink to={routes.adminErrorLogs.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-exclamation-triangle" />
              Error Logs
            </NavLink>
          )}

          {isAssistant && (
            <NavLink to={routes.assistantDashboard.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-clipboard-list" />
              {labels.myTasks}
            </NavLink>
          )}

          {isAssistant && (
            <NavLink to={routes.assistantTimeTracking.link} className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
              <i className="fas fa-clock" />
              {labels.workTime}
            </NavLink>
          )}
        </nav>

        <div className="admin-sidebar__footer">
          <NavLink to="/finish-profile" className="admin-sidebar__link" activeClassName="admin-sidebar__link--active">
            <i className="fas fa-cog" />
            {labels.settings}
          </NavLink>
          <button
            type="button"
            className="admin-sidebar__link admin-sidebar__link--logout"
            onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt" />
            {labels.logout}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
